import { HubConnectionBuilder, HubConnectionState, type HubConnection } from '@microsoft/signalr'
import type { Trade, Notification } from '@/types/domain'

// Server→client event payloads (see M4-WAIVERS-TRADES-CONTRACT §SignalR `/hubs/notifications`).
// The user is joined to their `user:{userId}` group via `JoinUser()` (the server derives the id
// from the cookie) and is also targeted via `team:{teamId}`/`league:{leagueId}` groups it already
// belongs to.

export interface WaiverProcessedEvent {
  claimId: string
  status: string
  addPlayerId: string
}

/** Callbacks a consumer (the notification store) wires up before connecting. */
export interface NotificationsHubHandlers {
  onWaiverProcessed?: (event: WaiverProcessedEvent) => void
  onTradeProposed?: (trade: Trade) => void
  onTradeResolved?: (trade: Trade) => void
  onNotification?: (notification: Notification) => void
  /** Fired after the transport reconnects; the server drops group membership, so re-join. */
  onReconnected?: () => void
  /** Fired on any connection-state transition so the UI can show online/offline. */
  onStatusChange?: (connected: boolean) => void
}

/**
 * Thin wrapper around a `/hubs/notifications` SignalR connection: cookie auth (`withCredentials`),
 * automatic reconnect, `JoinUser()` group join, and typed server events. Holds no notification
 * state itself — the store owns the list + unread count. Unlike the draft/scoring hubs there is no
 * per-league REST source of truth to re-pull; events drive the live list directly.
 */
export class NotificationsHub {
  private connection: HubConnection | null = null

  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected
  }

  /** Builds the connection, registers handlers, starts it, and joins the caller's user group. */
  async connect(handlers: NotificationsHubHandlers): Promise<void> {
    // Avoid duplicate connections.
    await this.disconnect()

    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/notifications', { withCredentials: true })
      .withAutomaticReconnect()
      .build()

    connection.on('WaiverProcessed', (event: WaiverProcessedEvent) =>
      handlers.onWaiverProcessed?.(event),
    )
    connection.on('TradeProposed', (trade: Trade) => handlers.onTradeProposed?.(trade))
    connection.on('TradeResolved', (trade: Trade) => handlers.onTradeResolved?.(trade))
    connection.on('Notification', (notification: Notification) =>
      handlers.onNotification?.(notification),
    )

    connection.onreconnecting(() => handlers.onStatusChange?.(false))
    connection.onreconnected(async () => {
      // Group membership is dropped on reconnect — re-join the user group.
      await this.joinUser()
      handlers.onStatusChange?.(true)
      handlers.onReconnected?.()
    })
    connection.onclose(() => handlers.onStatusChange?.(false))

    this.connection = connection

    await connection.start()
    await this.joinUser()
    handlers.onStatusChange?.(true)
  }

  /** Joins the caller's user group (server derives the user id from the cookie). */
  private async joinUser(): Promise<void> {
    const connection = this.connection
    if (!connection || connection.state !== HubConnectionState.Connected) return
    await connection.invoke('JoinUser')
  }

  /** Stops the connection (best-effort). */
  async disconnect(): Promise<void> {
    const connection = this.connection
    this.connection = null
    if (!connection) return
    await connection.stop()
  }
}

/** Factory so consumers (and tests) can inject an alternate implementation. */
export function createNotificationsHub(): NotificationsHub {
  return new NotificationsHub()
}
